import type { OverlayRef, PositionStrategy } from '@angular/cdk/overlay'
import type { ElementRef } from '@angular/core'
import { inject, Injectable } from '@angular/core'

import { DestroyablesQueue } from '@app/core/destroyables-queue'
import { VISIBILITY_ANIMATION_CONFIGS } from '@app/modules/animation'
import { FrameService, setupFrameComponentDismissSubscription } from '@app/modules/frame'
import type { Notification } from '@app/modules/notifications'

import { NotificationComponent } from './notification.component'

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly frameService: FrameService = inject(FrameService)

  private static id = 0

  private visibleNotifications: {
    id: number
    overlayRef: OverlayRef
    elementRef: ElementRef<HTMLElement>
  }[] = []

  private readonly queue = new DestroyablesQueue({ size: 3 })
  private readonly animationEnterQueue = new DestroyablesQueue({ size: 1 })

  show(notification: Notification): void {
    const id = NotificationService.id++
    this.animationEnterQueue.add(removeFromAnimationEnterQueue => {
      this.queue.add(removeFromMainQueue => {
        const { overlayRef, frameViewRef, viewRef } = this.frameService.attachView(
          NotificationComponent,
          {
            scrollStrategy: this.frameService.overlay.scrollStrategies.noop(),
            positionStrategy: this.getNextPositionStrategy(this.visibleNotifications.length),
            backdropCloseOnClick: false,
            backdropClickThrough: true,
            backdropStyle: 'invisible',
            padding: '8px 16px',
          },
          {
            mobile: {
              animationBeforeEnter: VISIBILITY_ANIMATION_CONFIGS.fadeTop,
              animationAfterLeave: VISIBILITY_ANIMATION_CONFIGS.fadeTop,
              width: '100%',
            },
            tablet: {
              animationBeforeEnter: VISIBILITY_ANIMATION_CONFIGS.slideRight,
              animationAfterLeave: VISIBILITY_ANIMATION_CONFIGS.slideRight,
              width: '392px',
            },
          },
        )

        this.visibleNotifications.push({
          id,
          overlayRef,
          elementRef: frameViewRef.instance.elementRef,
        })

        setupFrameComponentDismissSubscription(frameViewRef.instance, 6000)
        viewRef.instance.notification = notification
        frameViewRef.instance.animationShowFinished$.subscribe(() => {
          removeFromAnimationEnterQueue()
        })
        frameViewRef.instance.animationCloseFinished$.subscribe(() => {
          this.visibleNotifications = this.visibleNotifications.filter(element => element.id !== id)
          this.updateAllPositionStrategies()
          removeFromMainQueue()
        })
      })
    })
  }

  private updateAllPositionStrategies(): void {
    this.visibleNotifications.forEach(({ overlayRef }, index) => {
      overlayRef.updatePositionStrategy(this.getNextPositionStrategy(index))
    })
  }

  private getNextPositionStrategy(index: number): PositionStrategy {
    const prevElement = this.visibleNotifications[index - 1]
    if (prevElement === undefined) {
      return this.frameService.overlay.position().global().right().top('calc(var(--member-header-height, 0px) + 8px)')
    }

    return this.frameService.overlay
      .position()
      .flexibleConnectedTo(prevElement.elementRef)
      .withFlexibleDimensions(true)
      .withPush(false)
      .withPositions([
        {
          originX: 'end',
          originY: 'bottom',
          overlayX: 'end',
          overlayY: 'top',
        },
      ])
  }
}
